import type { Company } from '../types/company';
import { kryspaceCompanySlugHint } from './kryspaceSlugHint';

const METADATA_SLUG_KEYS = [
  'engineering_workspace_slug',
  'kryspace_workspace_slug',
  'kryspace_company_slug_hint',
] as const;

export type KryspaceSlugMetadataKey = (typeof METADATA_SLUG_KEYS)[number];

export type KryspaceSlugSource = 'kryspace_slug' | 'metadata' | 'name_hint';

export interface ResolvedKryspaceWorkspaceSlug {
  slug: string;
  source: KryspaceSlugSource;
  metadataKey?: KryspaceSlugMetadataKey;
}

function pickMetadataSlug(
  metadata: Record<string, unknown> | undefined
): { slug: string; key: KryspaceSlugMetadataKey } | undefined {
  if (!metadata) return undefined;
  for (const key of METADATA_SLUG_KEYS) {
    const raw = metadata[key];
    if (typeof raw === 'string' && raw.trim().length > 0) {
      return { slug: raw.trim(), key };
    }
  }
  return undefined;
}

/**
 * Map a platform company to its kryspace workspace folder slug.
 * Prefer `companies.kryspace_slug` (ADR 002), then Central supply metadata,
 * then display-name hint (Krybot prospecting rule).
 */
export function resolveKryspaceWorkspaceSlug(
  company: Pick<Company, 'name' | 'metadata' | 'kryspace_slug'>
): ResolvedKryspaceWorkspaceSlug {
  const dbSlug = company.kryspace_slug?.trim();
  if (dbSlug) {
    return { slug: dbSlug, source: 'kryspace_slug' };
  }
  const fromMeta = pickMetadataSlug(company.metadata);
  if (fromMeta) {
    return { slug: fromMeta.slug, source: 'metadata', metadataKey: fromMeta.key };
  }
  return { slug: kryspaceCompanySlugHint(company.name), source: 'name_hint' };
}
